/* eslint-disable guard-for-in */
/* eslint-disable no-restricted-syntax */
import Vue from 'vue';
import axios from 'axios';
import { i18n } from '@/plugins/I18n.js';
import { serialize } from 'object-to-formdata';
import { safelyParseJson } from '@/utils';
import ActiveAppsSelectionModal from '@/components/modals/ActiveAppsSelectionModal.vue';

export class APIError extends Error {
  constructor(response) {
    const { code } = response.errors[0]; // showing only first code for backwards compatibility
    super(code);

    this.response = response;
    this.code = code;
    this.translatedMessage = response.errors.map(({ translated }) => translated).join('\n');
  }
}

function checkBan(response) {
  const error = response.errors.find(e => e.code === 'ERROR_DEVICE_BANNED');
  if (!response.success && error) {
    if (Vue.router.currentRoute.name !== 'ban') {
      Vue.router.push({
        name: 'ban',
        params: {
          reason: response.data,
          heading: error.translated,
        },
      });
    }
  }
}

function checkPlusRequired(response) {
  const error = response.errors.find(e => e.code === 'ERROR_PLUS_REQUIRED');
  if (!error || !response.data) {
    return;
  }

  Vue.router.push({
    path: response.data,
  });

  throw new APIError(response);
}

function checkCustomerToken(response) {
  const isTokenInvalid = response.errors.some(e => e.code === 'ERROR_BROKEN_LINK_TOKEN');
  if (isTokenInvalid) {
    Vue.store.commit('lt', '');
  }
}

function checkPublisherToken(response) {
  const isTokenInvalid = response.errors.some(e => e.code === 'ERROR_INVALID_STAFF_TOKEN');
  if (isTokenInvalid) {
    Vue.store.commit('st', '');
  }
}

// function showMaintenanceError() {
//   Vue.prototype.$alert('appdb is in maintenance mode, please check <a href="https://status.dbservices.to">status page</a> for information.', {
//     okText: 'Refresh',
//   }).finally(() => window.location.reload());
// }

class Api {
  domain = 'https://api.dbservices.to/v1.7/';

  async call(action, data, handleErrors = true, returnFullResponse = false, config = {}) {
    let response = await axios({
      method: 'GET',
      url: `${this.domain}${action}/`,
      params: {
        lang: Vue.store.getters.lang,
        lt: Vue.store.getters.lt || '',
        st: Vue.store.getters.st || '',
        rc: Vue.store.getters.ref,
        ...data,
        brand: process.env.BRAND_CONFIG.name,
      },
      withCredentials: true,
      ...config,
    });

    response = response.data;

    checkBan(response);
    checkPlusRequired(response);
    checkCustomerToken(response);
    checkPublisherToken(response);

    if (!handleErrors) {
      return response;
    }

    if (!response.success) {
      if (response.errors.length && response.errors[0].code === 'ERROR_MAINTENANCE_MODE') {
        // eslint-disable-next-line no-restricted-globals
        location.href = `/maintenance#${Vue.store.state.theme}`;
        return null;
      }

      Vue.notify({
        type: 'error',
        text: response.errors[0].translated + (typeof response.data === 'string' && response.data ? `. ${response.data}` : ''),
      });

      throw new APIError(response);
    }

    if (returnFullResponse) {
      return response;
    }

    return response.data;
  }

  async post(action, data, handleErrors = true, config) {
    const form = serialize(data);

    for (const key of form.keys()) {
      if (!form.get(key)) {
        form.delete(key);
      }
    }

    let link = `${this.domain}${action}/?lt=${Vue.store.getters.lt || ''}&st=${Vue.store.getters.st || ''}&lang=${Vue.store.getters.lang}&brand=${process.env.BRAND_CONFIG.name}`;
    if (Vue.store.getters.refCode) {
      link += `&ref_code=${Vue.store.getters.refCode}`;
    }
    const response = await axios.post(link, form, config);

    const result = response.data;

    checkBan(result);

    if (handleErrors) {
      if (!result.success) {
        // Only show error notification if it's a real error
        // Check for cases we want to suppress
        const cancelledOp = data._isCancelled === true; // Special flag to indicate a cancelled operation

        if (!cancelledOp) {
          Vue.notify({
            type: 'error',
            text: result.errors[0].translated + (typeof result.data === 'string' && result.data ? `. ${result.data}` : ''),
          });
        }

        throw new Error(result.errors[0].code);
      }

      return result.data;
    }

    return result;
  }

  async search({
    type,
    price = 0,
    genre = 0,
    dev = 0,
    order = 'added',
    page = 1,
    q = '',
  }) {
    return this.call('search', {
      type,
      price,
      genre,
      dev,
      order,
      length: 25,
      start: (page - 1) * 25,
      q,
    });
  }

  searchIndex(filters = {}, config) {
    return this.call('search_index', filters, undefined, undefined, config);
  }

  async getGenres() {
    return this.call('list_genres');
  }

  async getNews({
    limit = 3,
    id = null,
  } = {}) {
    return this.call('get_pages', {
      category: 'news',
      length: limit,
      id,
    });
  }

  async getArticle(id) {
    return this.call('get_pages', {
      category: 'articles',
      id,
    });
  }

  async getAppDetails({
    type,
    trackid,
  }) {
    return this.call('search', {
      type,
      trackid,
    });
  }

  async getLinks({
    type,
    trackid,
  }) {
    return this.call('get_links', {
      type,
      trackids: [trackid],
    }, false);
  }

  async getRequests(page, filter = {}) {
    return this.call('get_publish_requests', {
      type: 'ios',
      length: 25,
      start: (page - 1) * 25,
      ...filter,
    });
  }

  async link({
    email = '',
    code = '',
    type = 'new',
  }, handleErrors = false) {
    return this.call('link', {
      email,
      link_code: code,
      type,
    }, handleErrors);
  }

  getTVLinkCode({ email }) {
    return this.call('get_tv_link_code', { email });
  }

  async getDevices(handleErrors = false) {
    return this.call('get_all_devices', {}, handleErrors);
  }

  async getLinkCode() {
    return this.call('get_link_code');
  }

  async getDeviceStatus({ sinceUuid } = {}) {
    const commands = await this.call('get_status', { since_uuid: sinceUuid });
    const parsedCommands = commands.map(command => ({ ...command, params: safelyParseJson(command.params, { defaultValue: {} }) }));
    return parsedCommands;
  }

  async clearCommands() {
    return this.call('clear');
  }

  async getUpdates() {
    const loc = window.location.href;
    const get = (ticket, resolve, reject) => {
      this.call('get_updates', { t: ticket }, false)
        .then((response) => {
          if (response.success) {
            resolve(response.data);
            return;
          }
          if (response.errors.some(e => e.code === 'ERROR_NOT_READY')) {
            const timeout = setTimeout(() => {
              get(ticket, resolve, reject);
            }, 2000);
            if (loc !== window.location.href) {
              clearTimeout(timeout);
            }
            return;
          }

          reject(response.errors);
        });
    };
    return this.call('get_update_ticket')
      .then(ticket => new Promise((resolve, reject) => get(ticket, resolve, reject)));
  }

  async getConfiguration(handleErrors = true) {
    return this.call('get_configuration', {}, handleErrors);
  }

  async saveConfiguration(config) {
    const params = {};
    Object.keys(config)
      .forEach((key) => {
        params[`params[${key}]`] = config[key];
      });

    const getCodeAndSave = () => Vue.prototype.$prompt(i18n.t('ENTER_OTP'))
      .then((code) => {
        params['params[2fa_code]'] = code;
        return this.post('configure', params);
      })
      .catch((e) => {
        if (e && e.message === 'ERROR_2FA_CODE_IS_WRONG') {
          return getCodeAndSave();
        }

        throw e;
      });

    return this.post('configure', params)
      .catch((e) => {
        if (e && e.message === 'ERROR_2FA_CODE_IS_REQUIRED') {
          return getCodeAndSave();
        }

        throw e;
      });
  }

  async installApp({
    id,
    type,
    features,
    isFromRepo,
    name,
    link,
    image,
    version,
    installationTicket,
    asIpaFile = false,
  }) {
    console.log('[DEBUG] installApp called with params:', {
      id, type, features, isFromRepo,
    });
    const options = {
      enable_features: features,
      type,
      installation_ticket: installationTicket,
      as_ipa_file: Number(asIpaFile),
    };


    if (!isFromRepo) {
      options.id = id;
    } else {
      options.bundle_id = id;
      options.name = name;
      options.link = link;
      options.image = image;
      options.version = version;
    }

    // Keep track if we're in a cancelled state
    let operationCancelled = false;

    const doInstall = async (additionalOptions = {}) => {
      console.log('[DEBUG] doInstall executing with additionalOptions:', additionalOptions);

      // Don't continue if we've been cancelled
      if (operationCancelled) {
        console.log('[DEBUG] Installation was already cancelled, returning early');
        return null; // Return null instead of an object with success: false
      }

      const installOptions = { ...options, ...additionalOptions };

      // If this is from a cancellation, mark it to suppress error notifications
      if (additionalOptions._isCancelled) {
        installOptions._isCancelled = true;
      }

      console.log('[DEBUG] Sending install request with options:', installOptions);
      const resp = await this.post('install', installOptions, false);
      console.log('[DEBUG] install API response:', { success: resp.success, errors: resp.errors });

      if (!resp.success) {
        if (resp.errors && resp.errors[0] && resp.errors[0].code === 'ERROR_ALL_APPLE_BUNDLE_IDS_ARE_BUSY') {
          // If we got here after the user already saw the active apps dialog and clicked Continue,
          // it means the current set of options didn't resolve the issue.
          // We should only block the modal from appearing again if there were actual
          // selections made (bundle_id specified or apps removed)

          // The previous logic was checking if additionalOptions had any keys, but
          // when a user clicks "Continue" without making any selections, additionalOptions is {},
          // which would prevent the modal from appearing again

          // Let's remove this check and allow the modal to appear again
          // This is normal behavior as Apple requires a specific app to be selected
          console.log('[DEBUG] Detected ERROR_ALL_APPLE_BUNDLE_IDS_ARE_BUSY');

          // Get active apps
          const activeApps = await this.call('get_active_apps');
          console.log('[DEBUG] Got active apps count:', activeApps?.length);

          if (!activeApps || !activeApps.length) {
            console.log('[DEBUG] No active apps, throwing APIError');
            throw new APIError(resp);
          }

          // Show popup with active apps list using the dedicated modal component
          return new Promise((resolve, reject) => {
            console.log('[DEBUG] Creating modal promise');
            let modalClosed = false;

            // Function to handle modal closing
            const handleCancelOrClose = () => {
              console.log('[DEBUG] handleCancelOrClose called');
              if (modalClosed) {
                console.log('[DEBUG] Modal already closed, ignoring');
                return; // Prevent multiple executions
              }
              modalClosed = true;
              operationCancelled = true; // Mark the entire operation as cancelled
              console.log('[DEBUG] Setting operationCancelled=true and resolving promise');

              // Simply resolve with null to indicate cancellation without triggering error flows
              resolve(null);
            };

            // Show the modal
            console.log('[DEBUG] Showing modal');
            Vue.prototype.$modal.show(ActiveAppsSelectionModal, {
              apps: activeApps,
              originalError: new APIError(resp),
              doInstall,
              resolve,
              handleCancel: handleCancelOrClose,
              reject,
            }, {
              classes: 'active-apps-modal',
              height: 'auto',
              width: '100%',
              maxWidth: 600,
              scrollable: true,
              clickToClose: false,
            });

            // Setup event listener for modal overlay clicks (outside modal)
            // Need to wait a bit for the modal to be fully rendered
            setTimeout(() => {
              console.log('[DEBUG] Setting up overlay click handler');
              const overlays = document.querySelectorAll('.vm--overlay');
              console.log('[DEBUG] Found overlays:', overlays?.length);
              if (overlays && overlays.length) {
                const modalOverlay = Array.from(overlays).find(
                  overlay => overlay.style.display !== 'none',
                );

                if (modalOverlay) {
                  console.log('[DEBUG] Found visible overlay');
                  const handleOutsideClick = (e) => {
                    console.log('[DEBUG] Overlay clicked, target:', e.target === modalOverlay ? 'overlay' : 'child');
                    // Only handle clicks directly on the overlay (not its children)
                    if (e.target === modalOverlay) {
                      handleCancelOrClose();
                      modalOverlay.removeEventListener('click', handleOutsideClick);
                    }
                  };

                  modalOverlay.addEventListener('click', handleOutsideClick);
                } else {
                  console.log('[DEBUG] No visible overlay found');
                }
              }
            }, 100);
          });
        }

        console.log('[DEBUG] API call failed but not with bundle error, throwing APIError');
        throw new APIError(resp);
      }

      console.log('[DEBUG] API call succeeded');
      Vue.store.dispatch('getConfig');
      return resp;
    };

    try {
      console.log('[DEBUG] Starting installation');
      const result = await doInstall();
      console.log('[DEBUG] Installation result:', result);

      // Check if the result is null, indicating cancellation
      if (result === null) {
        console.log('[DEBUG] Installation was cancelled by user, returning cancellation result');
        // Return a successful cancellation result instead of undefined
        // This ensures anyone working with the Promise gets a proper result
        return {
          success: true,
          cancelled: true,
          data: null,
        };
      }

      return result;
    } catch (error) {
      // If the operation was cancelled, don't propagate the error
      if (operationCancelled) {
        console.log('[DEBUG] Error caught but operation was cancelled, returning cancellation result');
        return {
          success: true,
          cancelled: true,
          data: null,
        };
      }

      console.error('[DEBUG] Installation error:', error);

      // For actual errors, let them propagate
      throw error;
    }
  }

  async reportLink({
    id,
    type,
    reason,
  }) {
    return this.call('report', {
      id,
      type,
      reason,
    });
  }

  uploadFile({ type, file }) {
    return this.post('upload_file', {
      type,
      file,
    });
  }

  async uploadApp({
    file,
    id,
  }, progressCb) {
    return this.post('add_ipa', {
      ipa: file,
      job_id: id,
    }, false, { onUploadProgress: progressCb });
  }

  async editIpaMetadata(metadata) {
    return this.post('edit_ipa_metadata', metadata, false);
  }

  async uploadAppByUrl({
    url,
    id,
  }) {
    return this.post('add_ipa', {
      url,
      job_id: id,
    });
  }

  async getAppsJobs() {
    return this.call('get_ipa_analyze_jobs');
  }

  async deleteAppJob(id) {
    return this.call('delete_ipa_analyze_job', { id });
  }

  getIpas(filters = {}, config) {
    return this.call('get_ipas', filters, undefined, undefined, config);
  }

  async deleteIpa(id) {
    return this.call('delete_ipa', { id });
  }

  getInstallationHistory(params = {}) {
    // Handle pagination parameters
    const requestParams = typeof params === 'object' && params !== null ? params : {};
    return this.call('get_installation_history', requestParams);
  }

  async getPlusPurchaseOptions() {
    return this.call('get_plus_purchase_options');
  }

  async getLanguages() {
    return this.call('get_languages');
  }

  async requestApp(url, version) {
    return this.call('create_publish_request', {
      type: 'ios',
      url,
      version,
    });
  }

  async setPublishRequestStatus({
    status, id, uoid, type,
  }) {
    return this.post('set_publish_request_status', {
      status,
      id,
      universal_object_identifier: uoid,
      type,
    });
  }

  async getRedirectParams(ticket) {
    return this.call('process_redirect', {
      t: ticket,
    });
  }

  async getRedirectLink(ticket) {
    return this.call('process_redirect', {
      rt: ticket,
    });
  }

  async getPromotions() {
    return this.call('get_promotions');
  }

  async getStats() {
    return this.call('get_stats');
  }

  async purchaseWithBTC({
    mode,
    txid,
    ticket,
  }) {
    return this.call('bitcoin_purchase', {
      mode,
      txid,
      purchase_ticket: ticket,
    });
  }

  async unlinkDevice(lt) {
    return this.call('unlink', {
      device_link_token: lt,
    });
  }

  notifyProfileRemoval() {
    return this.call('notify_profile_removal');
  }

  async getInstallFeatures() {
    return this.call('get_features');
  }

  getSubscriptions() {
    return this.call('get_subscriptions');
  }

  getTransactions() {
    return this.call('get_transactions');
  }

  getPurchases() {
    return this.call('get_purchases');
  }

  processPurchase({
    operation,
    purchaseTicket,
    appIdentifier,
  }) {
    return this.call('process_purchase', {
      operation,
      purchase_ticket: purchaseTicket,
      app_identifier: appIdentifier,
    });
  }

  requestRefund({
    identifier,
  }) {
    return this.call('request_refund', {
      identifier,
    });
  }

  requestCancelSubscription({
    identifier,
  }) {
    return this.call('request_cancel_subscription', {
      identifier,
    });
  }

  editPlusDevAccount({
    appleid,
    password,
    isSMSAuth,
    otp,
    status,
    sharedWithEmails,
  }) {
    return this.call('edit_plus_dev_account', {
      appleid,
      password,
      is_sms_auth: isSMSAuth,
      otp,
      status,
      shared_with_emails: sharedWithEmails,
    });
  }

  editFreeDevAccount({
    appleid,
    password,
    isSMSAuth,
    otp,
    status,
  }) {
    return this.call('edit_free_dev_account', {
      appleid,
      password,
      is_sms_auth: isSMSAuth,
      otp,
      status,
    });
  }

  deletePlusDevAccount() {
    return this.call('delete_plus_dev_account');
  }

  archiveRevokedPlusDevAccount() {
    return this.call('archive_revoked_plus_dev_account');
  }

  deleteFreeDevAccount() {
    return this.call('delete_free_dev_account');
  }

  getPlusDevAccountArchive() {
    return this.call('get_plus_dev_account_archive');
  }

  getFreeDevAccountArchive() {
    return this.call('get_free_dev_account_archive');
  }

  getDevCredentialsProviders() {
    return this.call('get_dev_credentials_providers');
  }

  getEnterpriseCertificates() {
    return this.call('get_enterprise_certs');
  }

  addRepo(url) {
    return this.post('edit_repo', {
      url,
    }, false);
  }

  editRepo({
    url,
    id,
    isPublic,
  }) {
    return this.post('edit_repo', {
      url,
      id,
      is_public: isPublic,
    });
  }

  deleteRepo(id) {
    return this.call('delete_repo', {
      id,
    });
  }

  getRepos(isPublic) {
    return this.call('get_repos', {
      is_public: isPublic,
    });
  }

  getRepo(id) {
    return this.call('get_repos', {
      id,
    }, false);
  }

  getDylibs() {
    return this.call('get_dylibs');
  }

  addDylib({ file, url } = {}) {
    return this.post('add_dylib', {
      dylib: file,
      url,
    });
  }

  deleteDylib(name) {
    return this.post('delete_dylib', {
      name,
    });
  }

  getSideloadingOptions(type) {
    return this.call('get_sideloading_options', {
      type,
    });
  }

  getP2POffers({ currency }) {
    return this.call('get_p2p_offers', { currency });
  }

  editP2POffer({ id, operation }) {
    return this.post('edit_p2p_offer', {
      id,
      operation,
    });
  }

  editPlusDevAccountP2PSettings({
    isP2P,
    p2pPrice,
    p2pName,
    p2pPaypalEmail,
    p2pCurrency,
    p2pManualInstructions,
  }) {
    return this.post('edit_plus_dev_account_p2p_settings', {
      is_p2p: isP2P,
      p2p_price: p2pPrice,
      p2p_name: p2pName,
      p2p_paypal_email: p2pPaypalEmail,
      p2p_currency: p2pCurrency,
      p2p_manual_instructions: p2pManualInstructions,
    });
  }

  getP2PPurchases(params) {
    return this.call('get_p2p_purchases', params, false);
  }

  banunbanDevice({
    udid,
    reason,
  }) {
    return this.call('banunban_device', { udid, reason });
  }

  changeEmail({ newEmail, ticket, code }) {
    return this.call('change_email', { new_email: newEmail, ticket, code });
  }

  universalGateway({ universalObjectId, id, silent }) {
    return this.call('universal_gateway', { universal_object_identifier: universalObjectId, id }, !silent);
  }

  importToLibrary(universalObjectId) {
    return this.call('import_to_library', { universal_object_identifier: universalObjectId });
  }

  getCustomerProfile() {
    return this.call('get_customer_profile', undefined, false);
  }

  getPublisherProfile() {
    return this.call('get_account_data', undefined, false);
  }

  getDefaults() {
    return this.call('get_defaults');
  }

  getProductMetadata({ identifier }) {
    return this.call('get_product_metadata', { identifier });
  }

  initPurchase({ productIdentifier }) {
    return this.call('init_purchase', { product_identifier: productIdentifier });
  }

  async getMessages({ topic, start = 0, length = 25 } = {}) {
    return this.post('get_messages', {
      topic,
      start,
      length,
    }, false);
  }

  async postMessage({
    topic, message, as_staff = 0, is_private = 0, reply_to_id = null,
  } = {}) {
    return this.post('post_message', {
      topic,
      message,
      as_staff,
      is_private,
      reply_to_id,
    });
  }

  async setMessageRead({ id, as_staff = 0 } = {}) {
    return this.post('set_message_read', {
      id,
      as_staff,
    });
  }

  async editCustomerProfile({ name } = {}) {
    return this.post('edit_customer_profile', {
      name,
    });
  }

  getWithdrawals() {
    return this.call('get_withdrawals', null, false);
  }

  getReimbursementBalances() {
    return this.call('get_reimbursement_balances', null, false);
  }

  getSupportTickets(params = {}) {
    // Handle both old format (just ID) and new format (object with pagination)
    const requestParams = typeof params === 'object' && params !== null ? params : { id: params };

    return this.post('get_support_tickets', requestParams, false);
  }

  createSupportTicket({ topic, message }) {
    return this.post('create_support_ticket', {
      topic,
      message,
    });
  }
}

const plugin = {
  install() {
    Vue.prototype.$api = new Api();
  },
};

export default plugin;
